import React, { useCallback, useEffect, useState } from "react";
// import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import Header from "../../components/header";

import { videoDataLists } from "../../dataLists/videoDataLists";
import { SendIcon } from "../../shared/icons";
import { videoDataListsType } from "../../shared/type";
import VideoCard from "../../components/VideoCard";
// Video card component with hover/mousedown preview
import { HelmetSeo } from "../../components/HelmetSeo";

// 輔助函數：獲取字符串的前兩個詞語
const getFirstTwoWords = (text: string): string => {
  if (!text || text.trim() === "") return "";
  const words = text
    .trim()
    .split(/\s+/)
    .filter((word) => word.length > 0);
  return words.slice(0, 2).join(" ");
};

export default function VideoSearchPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const tagsItem = queryParams.get("tags");
  const videoSearchDestination = location.state?.videoSearchDestination;

  // 初始化顯示值：如果有 tagsItem，只顯示前兩個詞；否則使用 videoSearchDestination 或空字符串
  const getInitialDisplayValue = (): string => {
    if (tagsItem) {
      return getFirstTwoWords(tagsItem);
    }
    return videoSearchDestination || "";
  };

  const [destination, setDestination] = useState(getInitialDisplayValue());
  const [videoPageData, setVideoPageData] = useState<videoDataListsType[]>();
  const [showAll, setShowAll] = useState(false);

  const INITIAL_DISPLAY_COUNT = 8;

  // 獲取默認推薦視頻（當沒有搜索條件時顯示）
  const getDefaultRecommendedVideos = useCallback(() => {
    const allVideos = [...videoDataLists];
    // 隨機排序並返回前 16 個作為默認推薦
    return allVideos.sort(() => 0.5 - Math.random()).slice(0, 8);
  }, []);

  const searchVideoData = useCallback(
    (destination: string) => {
      if (!destination || destination.trim() === "") {
        // 如果沒有搜索條件，顯示默認推薦視頻
        const defaultVideos = getDefaultRecommendedVideos();
        setVideoPageData(defaultVideos);
        return;
      }

      // 將搜索詞分解成單個詞語（按空格分割，並過濾空字符串）
      const searchWords = destination
        .trim()
        .split(/\s+/)
        .filter((word) => word.length > 0)
        .map((word) => word.toLowerCase());

      // 如果沒有有效的搜索詞，返回空結果
      if (searchWords.length === 0) {
        setVideoPageData([]);
        setShowAll(false);
        return;
      }

      // 匹配規則：videoDataLists 的 tag 是否包含任何一個搜索詞語
      const videoData = videoDataLists.filter((item: videoDataListsType) =>
        item.tags.some((tag) =>
          searchWords.some((word) => tag.toLowerCase().includes(word))
        )
      );

      setVideoPageData(videoData.sort(() => 0.5 - Math.random()));
      setShowAll(false); // 重置顯示狀態
    },
    [getDefaultRecommendedVideos]
  );

  // Update destination state and perform search when URL params or location state changes
  useEffect(() => {
    const currentTagsItem = new URLSearchParams(location.search).get("tags");
    const currentVideoSearchDestination =
      location.state?.videoSearchDestination;

    // 確定顯示值：如果有 tagsItem，只顯示前兩個詞；否則使用 videoSearchDestination
    let newDisplayValue = "";
    if (currentTagsItem) {
      newDisplayValue = getFirstTwoWords(currentTagsItem);
    } else if (currentVideoSearchDestination) {
      newDisplayValue = currentVideoSearchDestination;
    }

    // Update destination state if it changed (only from URL/state, not from user input)
    setDestination((prev: string) => {
      if (prev !== newDisplayValue) {
        return newDisplayValue;
      }
      return prev;
    });

    // Perform search based on URL params or location state
    // 搜索時使用完整的 tagsItem 或 videoSearchDestination
    if (
      currentVideoSearchDestination &&
      currentVideoSearchDestination.trim() !== ""
    ) {
      searchVideoData(currentVideoSearchDestination);
    } else if (
      currentTagsItem &&
      currentTagsItem.trim() !== "" &&
      !currentVideoSearchDestination
    ) {
      // 使用完整的 tagsItem 進行搜索
      searchVideoData(currentTagsItem);
    } else {
      // 如果沒有 tags 也沒有 videoSearchDestination，顯示默認推薦視頻
      // 這解決了用戶直接訪問 /videopage 時沒有數據的問題
      searchVideoData("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.state?.videoSearchDestination]);

  const handleSearchDestination = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (destination.trim() === "") {
      return;
    }
    // console.log("destination:", destination);
    // Update URL to persist search query
    navigate(`/videopage?tags=${encodeURIComponent(destination.trim())}`, {
      replace: true,
    });
    searchVideoData(destination);
  };

  const tags = new URLSearchParams(location.search).get("tags") || "";
  const seoTitle = tags
    ? `Videos about ${getFirstTwoWords(tags)} | Explore Asia`
    : "Videos | Explore Asia";

  const seoDesc = tags
    ? `Watch videos related to ${tags}. Explore Asia video recommendations and inspirations.`
    : "Watch travel and culture videos across Asia. Explore Asia video recommendations and inspirations.";

  return (
    <>
      <HelmetSeo title={seoTitle} description={seoDesc} ogType="website" />
      {/* <Helmet>
        <title>AI Travel Planner - Create Your Perfect Asian Journey</title>
        <meta
          name="description"
          content="Plan your perfect Asian trip with our AI-powered travel planner. Get personalized itineraries, local insights, and travel recommendations for your dream destination."
        />
      </Helmet> */}
      <Header />
      <div className="w-full h-full fixed top-0 left-0 -z-10 bg-white/60 bg-blend-overlay backdrop-blur-md"></div>

      <div className="w-full h-full flex justify-center items-center">
        <div className="w-full h-full flex flex-col my-14 pb-20 gap-y-8 px-8 2xl:max-w-[1500px] max-w-[1200px]">
          <form
            autoComplete="off"
            onSubmit={handleSearchDestination}
            className="w-full flex items-center gap-x-2 bg-white rounded-full xl:w-[60%] lg:w-[70%] md:max-w-[80%] sm:max-w-[90%] border-[1px] border-[#c9b077]/40"
          >
            <input
              type="text"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full lg:px-5 xl:px-7 px-5 md:py-[18px] py-4 lg:py-4 xl:py-5 rounded-full outline-none font-semibold lg:text-lg xl:text-xl text-base text-black placeholder:text-[#898989]"
              placeholder="Ask me any travel idea!"
            />
            <button
              type="submit"
              disabled={!destination.trim()}
              className={`md:mr-2 mr-1.5 flex-shrink-0 bg-[#181818] rounded-full lg:p-2.5 xl:p-4 p-2 relative ${
                !destination.trim() ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {!destination.trim() ? (
                <>
                  <span className="xl:scale-150 lg:scale-125 flex items-center justify-center opacity-50">
                    <SendIcon />
                  </span>
                </>
              ) : (
                <span className="xl:scale-150 lg:scale-125 flex items-center justify-center">
                  <SendIcon />
                </span>
              )}
            </button>
          </form>
          <div className=" gap-2 flex flex-wrap md:gap-4 px-8">
            {[
              "Travel",
              "Guides",
              "Food",
              "Hotels",
              "China",
              "Japan",
              "Recommended",
            ].map((item) => (
              <span
                key={item}
                className="py-1.5 px-3 bg-[#c9b077]/70 hover:bg-[#c9b077] text-white rounded-full text-sm md:text-base font-medium cursor-pointer"
                onClick={() => {
                  const trimmed = item.trim();
                  setDestination(trimmed);
                  // Update URL and trigger search when clicking on a quick tag
                  if (trimmed) {
                    navigate(`/videopage?tags=${encodeURIComponent(trimmed)}`, {
                      replace: true,
                    });
                    searchVideoData(trimmed);
                  }
                }}
              >
                {item}
              </span>
            ))}
          </div>
          {videoPageData !== undefined && videoPageData.length === 0 ? (
            <div className="w-full px-8 py-12 text-center">
              <p className="text-lg xl:text-2xl text-gray-600">
                No videos found for "{destination}". Try searching for something
                else!
              </p>
            </div>
          ) : videoPageData && videoPageData.length > 0 ? (
            <>
              {!destination || destination.trim() === "" ? (
                <div className="flex items-center pl-8 text-[#5c687a]">
                  <img
                    src="icon/hot.png"
                    alt="popular"
                    className="w-6 h-6 md:w-8 md:h-8 mr-1 flex-shrink-0 object-contain"
                  />
                  <span className="text-lg md:text-2xl font-bold">
                    Popular Videos:
                  </span>
                </div>
              ) : null}
              <div className="w-full px-8 grid 2xl:grid-cols-4 xl:grid-cols-3 lg:grid-cols-4 md:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-8">
                {(showAll
                  ? videoPageData
                  : videoPageData.slice(0, INITIAL_DISPLAY_COUNT)
                ).map((item: videoDataListsType) => (
                  <VideoCard key={item.id} item={item} />
                ))}
              </div>
              {videoPageData.length > INITIAL_DISPLAY_COUNT && !showAll && (
                <div className="w-full flex justify-center mt-4">
                  <button
                    onClick={() => setShowAll(true)}
                    className="px-8 py-3 bg-[#c9b077]/70 text-white rounded-full font-semibold text-base hover:bg-[#c9b077] transition-colors"
                  >
                    Show More ({videoPageData.length - INITIAL_DISPLAY_COUNT}{" "}
                    more videos)
                  </button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </div>
    </>
  );
}
